import { useDispatch } from 'react-redux';
import { combineReducers, type Reducer, type UnknownAction } from 'redux';
import type { ThunkDispatch } from 'redux-thunk';
import adrData from '@rsos/components/capstone/DataCards/adrDataSlice';
import { gisServices } from '@rsos/reduxDataStore';
import additionalLayers from '@rsos/reduxDataStore/src/features/additionalLayers/additionalLayersSlice';
import airAmbulance from '@rsos/reduxDataStore/src/features/airAmbulance/airAmbulanceSlice';
import chat from '@rsos/reduxDataStore/src/features/alerts/chatSlice';
import incidents from '@rsos/reduxDataStore/src/features/alerts/incidentsSlice';
import alertsRouting from '@rsos/reduxDataStore/src/features/alertsRouting/alertsRoutingSlice';
import appLauncher from '@rsos/reduxDataStore/src/features/appLauncher/appLauncherSlice';
import audioNotifications from '@rsos/reduxDataStore/src/features/audioNotifications/audioNotificationsSlice';
import psaps from '@rsos/reduxDataStore/src/features/capstone/psapInfo/psapInfoSlice';
import chats from '@rsos/reduxDataStore/src/features/chats/chatsSlice';
import conversations from '@rsos/reduxDataStore/src/features/conversations/conversationsSlice';
import { displaySchemaApi } from '@rsos/reduxDataStore/src/features/displaySchema/displaySchemaApi';
import emergencyCalls from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import eventPane from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import eventShare from '@rsos/reduxDataStore/src/features/eventShare/eventShareSlice';
import geoSelection from '@rsos/reduxDataStore/src/features/geoSelection/geoSelectionSlice';
import gisSearch from '@rsos/reduxDataStore/src/features/gisSearch/gisSearchSlice';
import { getSuggestedAddressesApi } from '@rsos/reduxDataStore/src/features/gisSearch/helpers';
import harmonyChat from '@rsos/reduxDataStore/src/features/harmonyChat/harmonyChatSlice';
import hoveredIncident from '@rsos/reduxDataStore/src/features/incidentInteractions/hoveredIncidentSlice';
import {
  keywordsSetsReducer as keywordSets,
  keywordsTiersReducer as keywordTiers,
} from '@rsos/reduxDataStore/src/features/keywords';
import layerOpacity from '@rsos/reduxDataStore/src/features/layerOpacity/layerOpacitySlice';
import mapCard from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import media from '@rsos/reduxDataStore/src/features/media/mediaSlice';
import norfolkSouthern from '@rsos/reduxDataStore/src/features/norfolkSouthern/norfolkSouthernSlice';
import outboundSms from '@rsos/reduxDataStore/src/features/outboundSms/outboundSmsSlice';
import peerAgencies from '@rsos/reduxDataStore/src/features/peerAgencies/peerAgenciesSlice';
import queue from '@rsos/reduxDataStore/src/features/queue/queueSlice';
import rcs from '@rsos/reduxDataStore/src/features/rcs/rcsSlice';
import { resourceRequests } from '@rsos/reduxDataStore/src/features/resourceRequests';
import { rsosApi } from '@rsos/reduxDataStore/src/features/rsosApi';
import talkjsMessages from '@rsos/reduxDataStore/src/features/talkjsMessages/talkjsMessagesSlice';
import textFrom911 from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Slice';
import videoTo911 from '@rsos/reduxDataStore/src/features/videoTo911/videoTo911Slice';
import what3words from '@rsos/reduxDataStore/src/features/what3words/what3wordsSlice';
import schemas from '@rsos/schema-storage';
import { sinatra } from '@rsos/sinatra';
import avive from '../components/JurisdictionView/AviveAED/aviveSlice';
import { transcriptFeedbackApi } from '../components/JurisdictionView/EventPane/tabs/TranscriptTab/TranscriptFeedback/transcriptFeedbackApi';
import textToVoice from '../components/JurisdictionView/EventPane/tabs/TranscriptTab/TranscriptViewer/textToVoiceSlice';
import transcriptions, {
  transcriptionApiReducer as transcriptionApi,
} from '../components/JurisdictionView/EventPane/tabs/TranscriptTab/transcriptionSlice';
import radios, {
  radiosApiReducer as radiosApi,
} from '../components/JurisdictionView/EventPane/tabs/radiosSlice';
import keywordNotifications from '../components/JurisdictionView/KeywordNotification/keywordNotificationsSlice';
import mapMarkup from '../components/JurisdictionView/Map/mapMarkupSlice';
import rade from '../components/JurisdictionView/RADECard/radeSlice';
import search from '../components/JurisdictionView/UniversalSearch/searchSlice';
import axon from '../components/Map/useDeckGL/Axon/axonSlice';
import clickToIdentify from '../components/Map/useDeckGL/ClickToIdentifyGL/clickToIdentifySlice';
import eagleEye from '../components/Map/useDeckGL/EagleEye/eagleEyeSlice';
import mapPopover from '../components/Map/useDeckGL/MapPopover/mapPopoverSlice';
import app from '../reducers/appReducer';
import geofences from '../reducers/geofences';
import medicalert from '../reducers/medicalert';
import spatialLayers from '../reducers/spatialLayers';
import interagency from '../reduxDataStore/interagency/interagencySlice';

type ReducersWithTypes<K extends string | number | symbol> = Record<
  K,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Reducer<any, UnknownAction>
>;

/**
 * A no-op version of combineReducers that enforces all reducers have proper
 * types. This allows us to use compile-time verification on a line-by-line
 * basis to determine which reducers don't have types. This will cause
 * TypeScript errors for:
 * - Modules without default exports (need .d.ts fix)
 * - Non-reducer values
 *
 * Without this helper, combineReducers just causes an undecernable error for
 * the whole arg rather than the specific reducer causing problems.
 * NOTE: Interestingly, because we have allowJs enabled in our root tsconfig,
   when TypeScript processes even the JavaScript slice files, those that are
   using typed reducer creators such as createSlice from @reduxjs/toolkit,
   TypeScript will often still infer a reasonable type for the reducer even
   without an explicit .d.ts file. So this helper will catch untyped reducers,
   but isn't requiring us to port all of our reducers to TS immediately.
 */
function combineReducersTypeChecked<T extends ReducersWithTypes<keyof T>>(
  reducers: T,
) {
  return combineReducers(reducers);
}

const rootReducer = combineReducersTypeChecked({
  additionalLayers,
  adrData,
  airAmbulance,
  alertsRouting,
  app,
  appLauncher,
  avive,
  axon,
  chat,
  chats,
  clickToIdentify,
  conversations,
  displaySchemaApi: displaySchemaApi.reducer,
  eagleEye,
  emergencyCalls,
  eventPane,
  eventShare,
  geofences,
  geoSelection,
  getSuggestedAddressesApi: getSuggestedAddressesApi.reducer,
  gisSearch,
  gisServices,
  harmonyChat,
  hoveredIncident,
  incidents,
  interagency,
  keywordSets,
  keywordTiers,
  keywordNotifications,
  mapCard,
  mapMarkup,
  mapPopover,
  media,
  medicalert,
  norfolkSouthern,
  outboundSms,
  peerAgencies,
  psaps,
  rade,
  radiosApi,
  radios,
  api: rsosApi.reducer,
  schemas,
  search,
  spatialLayers,
  sinatra,
  talkjsMessages,
  textFrom911,
  audioNotifications,
  textToVoice,
  transcriptionApi,
  transcriptFeedbackApi: transcriptFeedbackApi.reducer,
  transcriptions,
  videoTo911,
  what3words,
  rcs,
  resourceRequests,
  queue,
  layerOpacity,
});

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = ThunkDispatch<RootState, unknown, UnknownAction>;
export const useAppDispatch = () => useDispatch<AppDispatch>();
export default rootReducer;
